// "Mes gains" — the Confirmation Agent's OWN earnings (2026-08-11).
//
// Until now an agent could not see what they had earned at all: the earnings
// view lived inside /admin/users behind a full-admin gate. Meanwhile the
// dashboard's "Commandes confirmées" card showed them EVERY agent's
// confirmations, and the API behind it shipped every agent's locked commission
// rate. So agents could see everyone's work except their own money.
//
// This card is the mirror image: strictly their own numbers, and nobody else's.
// Both queries it makes are scoped SERVER-SIDE —
//   • usersApi.myAgentDashboard() takes the agent id from the JWT, not the URL
//   • ordersApi.confirmations() filters to the caller for any non-admin
// so there is no client-side filtering here to get wrong or bypass.
//
// The confirmations query shares its key with ConfirmedOrdersCard, so on an
// admin screen React Query serves both cards from one request.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ordersApi, usersApi } from '@/lib/api';
import { useT } from '@/lib/i18n';

const GOLD = '#C9A24A';
const mad = (n: number) => `${Math.round(n).toLocaleString('fr-FR')} MAD`;

export function AgentEarningsCard({ range }: { range?: { from?: string; to?: string } }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);

  // All-time earnings (paid vs unpaid). No date range: what you are owed does
  // not depend on which window the dashboard is showing.
  const dash = useQuery({
    queryKey: ['my-agent-dashboard'],
    queryFn: () => usersApi.myAgentDashboard(),
    staleTime: 60_000,
  });

  // Period confirmations — same key as ConfirmedOrdersCard so the two dedupe.
  const feed = useQuery({
    queryKey: ['confirmations', range?.from ?? null, range?.to ?? null],
    queryFn: () => ordersApi.confirmations({ from: range?.from, to: range?.to }),
    staleTime: 60_000,
  });

  const earnings = dash.data?.earnings;
  const rows = feed.data?.confirmations ?? [];
  const periodCount = feed.data?.count ?? 0;
  const loading = dash.isLoading;

  return (
    <div
      className="mb-7 bg-white rounded-xl border overflow-hidden animate-[fadeInUp_0.5s_ease-out_both]"
      style={{ borderColor: 'rgba(201, 162, 74, 0.15)', boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04)' }}
    >
      {dash.error && (
        <div className="px-5 py-4">
          <p className="text-sm font-medium" style={{ color: 'var(--color-status-stuck)' }}>
            {t('Gains indisponibles')}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {t('Aucun chiffre affiché plutôt qu’un chiffre faux. Réessayez plus tard.')}
          </p>
        </div>
      )}

      {!dash.error && (
        <>
          <div className="px-5 pt-4 pb-1">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
              {t('Mes gains')}
            </p>
          </div>

          {/* Total earned, then the split that actually matters to the agent:
              what has been paid and what is still owed. */}
          <div className="px-5 pb-4 grid grid-cols-2 gap-4">
            <div>
              <p
                className="text-[26px] leading-none"
                style={{ color: 'var(--color-adm-ink)', fontFamily: "'Fraunces', Georgia, serif", fontWeight: 500 }}
              >
                {loading ? '…' : mad(earnings?.total ?? 0)}
              </p>
              <p className="text-[11px] text-gray-400 mt-1.5">{t('Gagné au total')}</p>
            </div>
            <div>
              <p
                className="text-[26px] leading-none"
                style={{
                  color: (earnings?.unpaid ?? 0) > 0 ? 'var(--color-status-waiting)' : 'var(--color-status-done)',
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontWeight: 500,
                }}
              >
                {loading ? '…' : mad(earnings?.unpaid ?? 0)}
              </p>
              <p className="text-[11px] text-gray-400 mt-1.5">
                {(earnings?.unpaid ?? 0) > 0 ? t('Pas encore payé') : t('Tout est payé')}
              </p>
            </div>
          </div>

          <div className="px-5 pb-4 flex flex-wrap gap-2 text-[11px]">
            <span className="px-2.5 py-1 rounded-full bg-[#DCFCE7] dark:bg-status-done-bg" style={{ color: 'var(--color-status-done)' }}>
              {t('Payé')} {mad(earnings?.paid ?? 0)}
            </span>
            <span className="px-2.5 py-1 rounded-full text-[#6B5423] dark:text-adm-gold-ink" style={{ background: 'rgba(201,162,74,0.10)' }}>
              {t('{n} confirmées sur la période', { n: periodCount })}
            </span>
            {(earnings?.unpaid_count ?? 0) > 0 && (
              <span className="px-2.5 py-1 rounded-full bg-[#FEF9C3] dark:bg-status-waiting-bg text-[#854D0E] dark:text-status-waiting">
                {t('{n} en attente de paiement', { n: earnings?.unpaid_count ?? 0 })}
              </span>
            )}
          </div>

          {/* Drill-down: the orders THIS agent confirmed, with dates — same
              pattern as the admin's per-agent view. */}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="w-full px-5 py-3 border-t flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            style={{ borderColor: 'rgba(201, 162, 74, 0.15)' }}
            aria-expanded={open}
          >
            <span className="text-xs text-gray-500">{t('Mes commandes confirmées')}</span>
            <span className="text-xs font-semibold" style={{ color: GOLD }}>
              {open ? t('Masquer') : t('Voir la liste')}
            </span>
          </button>

          {open && (
            <div className="border-t" style={{ borderColor: 'rgba(201, 162, 74, 0.15)' }}>
              {feed.error && (
                <p className="px-5 py-4 text-sm text-red-600">{(feed.error as Error).message}</p>
              )}
              {!feed.error && rows.length === 0 && (
                <p className="px-5 py-4 text-sm text-gray-500">
                  {t('Aucune commande confirmée sur cette période.')}
                </p>
              )}
              {rows.length > 0 && (
                <div className="max-h-[420px] overflow-y-auto">
                  {rows.map((r) => (
                    <div
                      key={r.order_id}
                      className="px-5 py-3 border-b last:border-b-0 flex items-center gap-3"
                      style={{ borderColor: 'rgba(201, 162, 74, 0.10)' }}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate" style={{ color: 'var(--color-adm-ink)' }}>
                          {r.shopify_order_id ? (
                            <Link to={`/admin/orders/${r.shopify_order_id}`} className="hover:underline">
                              {r.order_name ?? r.shopify_order_id}
                            </Link>
                          ) : (
                            (r.order_name ?? '—')
                          )}
                          {r.bundle && (
                            <span
                              className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded"
                              style={{ background: 'var(--color-adm-surface-3)', color: 'var(--color-adm-muted)' }}
                            >
                              {r.bundle}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{r.customer_name ?? '—'}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-semibold tabular-nums" style={{ color: 'var(--color-status-done)' }}>
                          {mad(r.rate ?? 0)}
                        </div>
                        <div className="text-[11px] text-gray-400 tabular-nums">
                          {new Date(r.confirmed_at).toLocaleDateString('fr-FR', {
                            day: '2-digit', month: '2-digit', year: '2-digit',
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="px-5 py-2.5 border-t bg-[#FAFAF8] dark:bg-adm-surface-2" style={{ borderColor: 'rgba(201,162,74,0.12)' }}>
            <p className="text-[10.5px] text-gray-500">
              {t('Une commande compte dès que vous cliquez « Confirmer la commande ». Le montant est fixé à ce moment-là et ne change plus.')}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
